import is from '@sindresorhus/is';
import { z } from 'zod';

import { type SkillProficiencyLevel, skillProficiencyLevelSchema } from '~/shared/data-contract/skill';
import { type LanguageCodeForJSA, languageCodeSchemaForJSA } from '~/shared/internationalization';

import {
    createUrlTileDefinition,
    serializeUrlTilePayloads,
    URL_TILE_SEARCH_PARAM,
    type UrlTileLegacyRewriter,
    type UrlTileModule,
    type UrlTileRenderContext,
} from '../url-tiles';
import { SkillsDetailsPanel } from './skill-details/SkillsDetailsPanel';

export const SKILL_DETAIL_TILE_SLOT_KEY = 'skill.detail';
export const SKILL_DETAIL_TILE_TYPE = 'v1.skill.detail';

const skillDetailTileSchema = z.object({
    preferredLanguage: languageCodeSchemaForJSA.optional(),
    skillId: z.uuid(),
    proficiency: skillProficiencyLevelSchema.optional(),
    type: z.literal(SKILL_DETAIL_TILE_TYPE),
});

export type SkillDetailTilePayload = z.infer<typeof skillDetailTileSchema>;

export const createSkillDetailTilePayload = ({
    preferredLanguage,
    skillId,
    proficiency,
}: {
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly skillId: string;
    readonly proficiency?: SkillProficiencyLevel;
}): SkillDetailTilePayload => ({
    preferredLanguage,
    skillId,
    proficiency,
    type: SKILL_DETAIL_TILE_TYPE,
});

const parseSkillDetailTokens = (tokens: readonly string[]): unknown => {
    const langIndex = tokens.indexOf('lang');
    const proficiencyIndex = tokens.indexOf('proficiency');
    const rawProficiency = proficiencyIndex >= 0 ? tokens[proficiencyIndex + 1] : undefined;

    return {
        preferredLanguage: langIndex >= 0 ? tokens[langIndex + 1] : undefined,
        proficiency: is.string(rawProficiency) ? Number.parseInt(rawProficiency, 10) : undefined,
        skillId: tokens[3],
        type: tokens.slice(0, 3).join('.'),
    };
};

const serializeSkillDetailTilePayload = (payload: SkillDetailTilePayload): string =>
    [
        payload.type,
        payload.skillId,
        is.string(payload.preferredLanguage) ? `lang.${payload.preferredLanguage}` : undefined,
        is.number(payload.proficiency) ? `proficiency.${payload.proficiency}` : undefined,
    ]
        .filter(is.string)
        .join('.');

const createSkillDetailTileProps = (payload: SkillDetailTilePayload, context: UrlTileRenderContext) => ({
    disableArrows: true,
    id: payload.skillId,
    onClose: () => context.closeTile(SKILL_DETAIL_TILE_SLOT_KEY),
    onNavigate: (skillId: string) => {
        context.updateTile(
            createSkillDetailTilePayload({
                preferredLanguage: payload.preferredLanguage,
                skillId,
                proficiency: payload.proficiency,
            }),
        );
    },
    onPreferredLanguageChange: (preferredLanguage: LanguageCodeForJSA) => {
        context.updateTile(
            createSkillDetailTilePayload({
                preferredLanguage,
                skillId: payload.skillId,
                proficiency: payload.proficiency,
            }),
        );
    },
    proficiency: payload.proficiency,
    preferredLanguage: payload.preferredLanguage,
});

export const skillDetailUrlTileDefinition = createUrlTileDefinition({
    maxWidth: '600px',
    minWidth: '600px',
    parseTokens: parseSkillDetailTokens,
    render: (props: ReturnType<typeof createSkillDetailTileProps>) => <SkillsDetailsPanel {...props} />,
    schema: skillDetailTileSchema,
    serialize: serializeSkillDetailTilePayload,
    slotKey: () => SKILL_DETAIL_TILE_SLOT_KEY,
    tileKey: (payload) => `${SKILL_DETAIL_TILE_SLOT_KEY}.${payload.skillId}`,
    toProps: createSkillDetailTileProps,
    type: SKILL_DETAIL_TILE_TYPE,
});

const skillDetailLegacySearchParamKeys = [
    'details.skills',
    'details.skills.id',
    'details.skills.preferredLanguage',
    // Old bookmarks may carry a proficiency; intentionally dropped — the panel derives it.
    'details.skills.proficiency',
] as const;

const hasSkillDetailLegacySearchParams = (searchParams: URLSearchParams): boolean => {
    return skillDetailLegacySearchParamKeys.some((key) => searchParams.has(key));
};

const deleteSkillDetailLegacySearchParams = (searchParams: URLSearchParams): void => {
    for (const key of skillDetailLegacySearchParamKeys) {
        searchParams.delete(key);
    }
};

const parseLegacySkillDetailPayloads = (searchParams: URLSearchParams): readonly SkillDetailTilePayload[] => {
    const skillDetailsType = searchParams.get('details.skills');
    const skillId = searchParams.get('details.skills.id');
    const preferredLanguage = searchParams.get('details.skills.preferredLanguage') ?? undefined;
    const parsedPayload = skillDetailTileSchema.safeParse({
        preferredLanguage,
        skillId,
        type: skillDetailsType === 'skills' ? SKILL_DETAIL_TILE_TYPE : undefined,
    });

    return parsedPayload.success ? [parsedPayload.data] : [];
};

export const skillDetailLegacyUrlTileRewriter: UrlTileLegacyRewriter = {
    rewrite: ({ definitions, hadCanonicalTileParams, searchParams }) => {
        if (!hasSkillDetailLegacySearchParams(searchParams)) {
            return undefined;
        }

        const nextParams = new URLSearchParams(searchParams);
        deleteSkillDetailLegacySearchParams(nextParams);

        if (hadCanonicalTileParams) {
            return nextParams;
        }

        for (const value of serializeUrlTilePayloads(parseLegacySkillDetailPayloads(searchParams), definitions)) {
            nextParams.append(URL_TILE_SEARCH_PARAM, value);
        }

        return nextParams;
    },
};

export const skillDetailUrlTileModule: UrlTileModule = {
    definitions: [skillDetailUrlTileDefinition],
    legacyRewriters: [skillDetailLegacyUrlTileRewriter],
};
